(() => {
  const API_NAME = 'NordwegCartDrawerLoader';
  const CONFIG_SELECTOR = 'script[data-cart-drawer-loader]';
  const INTENT_SELECTOR = '[data-cart-drawer-trigger], product-form [name="add"]';

  if (window[API_NAME]?.refresh) {
    window[API_NAME].refresh();
    return;
  }

  const assetUrls = new Map();
  const assetPromises = new Map();

  function refresh() {
    assetUrls.clear();
    document.querySelectorAll(CONFIG_SELECTOR).forEach((config) => {
      if (config.dataset.cartDrawerScript) {
        assetUrls.set('cartDrawerScript', config.dataset.cartDrawerScript);
      }
      if (config.dataset.govxScript) assetUrls.set('govxScript', config.dataset.govxScript);
      if (config.dataset.deliveryEstimateScript) {
        assetUrls.set('deliveryEstimateScript', config.dataset.deliveryEstimateScript);
      }
    });
  }

  function loadScript(url) {
    if (assetPromises.has(url)) return assetPromises.get(url);

    const promise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = url;
      script.async = true;
      script.dataset.cartDrawerRuntime = '';

      const settle = (event) => {
        script.removeEventListener('load', settle);
        script.removeEventListener('error', settle);
        if (event.type === 'load') {
          resolve();
          return;
        }
        script.remove();
        reject(new Error(`Failed to load cart runtime: ${url}`));
      };

      script.addEventListener('load', settle);
      script.addEventListener('error', settle);
      (document.head || document.documentElement).appendChild(script);
    }).catch((error) => {
      assetPromises.delete(url);
      throw error;
    });

    assetPromises.set(url, promise);
    return promise;
  }

  function isReady() {
    const drawer = document.querySelector('cart-drawer');
    return Boolean(
      customElements.get('cart-drawer')
      && drawer
      && typeof drawer.open === 'function'
      && typeof drawer.renderContents === 'function'
    );
  }

  async function load() {
    refresh();
    const drawerUrl = assetUrls.get('cartDrawerScript');
    if (!drawerUrl) throw new Error('Cart drawer runtime URL is unavailable.');

    const govxUrl = assetUrls.get('govxScript');
    if (govxUrl) loadScript(govxUrl).catch((error) => console.error(error));
    const deliveryEstimateUrl = assetUrls.get('deliveryEstimateScript');
    if (deliveryEstimateUrl) loadScript(deliveryEstimateUrl).catch((error) => console.error(error));
    await loadScript(drawerUrl);
    if (!isReady()) throw new Error('Cart runtime loaded without registering the drawer.');
    return document.querySelector('cart-drawer');
  }

  function closestIntentTarget(target) {
    return target instanceof Element ? target.closest(INTENT_SELECTOR) : null;
  }

  function warmFromIntent(event) {
    if (!closestIntentTarget(event.target) || isReady()) return;
    load().catch(() => false);
  }

  function shouldEnhanceClick(event) {
    return !event.defaultPrevented
      && event.button === 0
      && !event.metaKey
      && !event.ctrlKey
      && !event.shiftKey
      && !event.altKey;
  }

  function handleCartTriggerClick(event) {
    const trigger = event.target instanceof Element
      ? event.target.closest('[data-cart-drawer-trigger]')
      : null;
    if (!trigger || isReady() || !shouldEnhanceClick(event)) return;

    event.preventDefault();
    const fallbackUrl = trigger.href || trigger.getAttribute('href');
    load()
      .then((drawer) => drawer.open(trigger))
      .catch((error) => {
        console.error(error);
        if (fallbackUrl) window.location.assign(fallbackUrl);
      });
  }

  const api = { isReady, load, refresh };
  window[API_NAME] = api;
  refresh();

  document.addEventListener('pointerover', warmFromIntent, { passive: true, capture: true });
  document.addEventListener('pointerdown', warmFromIntent, { passive: true, capture: true });
  document.addEventListener('touchstart', warmFromIntent, { passive: true, capture: true });
  document.addEventListener('focusin', warmFromIntent, true);
  document.addEventListener('click', handleCartTriggerClick, true);
})();
